import { useState, useMemo, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useFavorites, FavoriteItem } from '../../../hooks/useFavorites';
import FavoriteButton from '../../../components/feature/FavoriteButton';
import FavoritesPanel from '../../../components/feature/FavoritesPanel';
import IngredientFilter from './IngredientFilter';
import BurgerNoteModal from '../../../components/feature/BurgerNoteModal';
import Toast, { useToast } from '../../../components/feature/Toast';

// Données des menus PDF
const menuCards = [
  { id: 'pizzas-classiques', name: 'Pizzas Classiques', imageUrl: 'https://static.readdy.ai/image/2dcf709829f9212ac45256ebe1ad3ecc/5a3bf2549f624a3d3ee05ccbfa8baa30.png' },
  { id: 'pizzas-speciales', name: 'Pizzas Spéciales', imageUrl: 'https://static.readdy.ai/image/2dcf709829f9212ac45256ebe1ad3ecc/4f8a922d8733d6eddfa34cae7da6a8ac.png' },
  { id: 'burgers-desserts', name: 'Burgers & Desserts', imageUrl: 'https://static.readdy.ai/image/2dcf709829f9212ac45256ebe1ad3ecc/df57b99ff08c0d7d17a95ee087458c7a.png' },
  { id: 'boissons', name: 'Boissons', imageUrl: 'https://static.readdy.ai/image/2dcf709829f9212ac45256ebe1ad3ecc/f1b4b2118a819a26d83ea3c4e73e15f6.png' },
];

// Composant pour surligner les ingrédients filtrés
function HighlightedIngredients({ 
  text, 
  included, 
  excluded 
}: { 
  text: string; 
  included: string[]; 
  excluded: string[]; 
}) {
  if (included.length === 0 && excluded.length === 0) {
    return <>{text}</>;
  }

  const parts = text.split(',').map(part => part.trim());
  
  return (
    <>
      {parts.map((part, index) => {
        const isIncluded = included.some(ing => 
          part.toLowerCase().includes(ing.toLowerCase())
        );
        
        return (
          <span key={index}>
            {index > 0 && ', '}
            <span className={isIncluded ? 'text-terracotta font-semibold' : ''}>
              {part}
            </span>
          </span>
        );
      })}
    </>
  );
}

const Menu = forwardRef((props, ref) => {
  const [mainCategory, setMainCategory] = useState<string | null>(null);
  const [pizzaSubCategory, setPizzaSubCategory] = useState<string | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [includedIngredients, setIncludedIngredients] = useState<string[]>([]);
  const [excludedIngredients, setExcludedIngredients] = useState<string[]>([]);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [previewCard, setPreviewCard] = useState<{ id: string; name: string; imageUrl: string } | null>(null);
  
  // État pour la modal de note burger
  const [burgerNoteModalOpen, setBurgerNoteModalOpen] = useState(false);
  const [pendingBurgerNote, setPendingBurgerNote] = useState<{
    item: any;
    category: string;
  } | null>(null);
  
  // Toast hook
  const { toast, showToast, hideToast } = useToast();
  
  const { favorites, addFavorite, removeFavorite, isFavorite, clearFavorites } = useFavorites();

  const tomatePizzas = [
    { name: 'Marguerita', ingredients: 'Sauce tomate, fromage, olives', normal: '9€', grande: '17€' },
    { name: 'Napoli', ingredients: 'Sauce tomate, anchois, fromage, olives', normal: '9,50€', grande: '17,50€' },
    { name: 'Capri', ingredients: 'Sauce tomate, jambon, fromage, olives', normal: '10€', grande: '18€' },
    { name: 'Régina', ingredients: 'Sauce tomate, jambon, champignons, fromage, olives', normal: '11€', grande: '19€' },
    { name: 'Paysanne', ingredients: 'Sauce tomate, lard, champignons, oignons, fromage, olives', normal: '11,50€', grande: '19,50€' },
    { name: 'Campagnarde', ingredients: 'Sauce tomate, jambon, lard, champignons, oignons, fromage, crème, olives', normal: '12€', grande: '20€' },
    { name: 'Chèvre', ingredients: 'Sauce tomate, champignons, chèvre, fromage, olives', normal: '11€', grande: '19€' },
    { name: '4 Fromages', ingredients: 'Sauce tomate, mozzarella, munster, chèvre, emmental, olives', normal: '12€', grande: '20€' },
    { name: 'Rustique', ingredients: 'Sauce tomate, viande hachée, oignons, chèvre, fromage, olives', normal: '12€', grande: '20€' },
    { name: '4 Saisons', ingredients: 'Sauce tomate, tomate fraîche, champignons, coeur d\'artichaut, poivrons, fromage, olives', normal: '11,50€', grande: '19,50€' },
    { name: 'Calabraise', ingredients: 'Sauce tomate, jambon, champignons, oignons, chorizo, fromage, olives', normal: '11,50€', grande: '19,50€' },
    { name: 'Calzone (chausson)', ingredients: 'Sauce tomate, jambon, champignons, fromage, oeuf', normal: '11€' },
    { name: 'Volaille', ingredients: 'Sauce tomate, poulet, oignons, fromage, origan, olives', normal: '11€', grande: '19€' },
    { name: 'Exotique (sucré salé)', ingredients: 'Sauce tomate, champignons, chèvre, poulet, miel, fromage, olives', normal: '12€', grande: '20€' },
    { name: 'Fruits de Mer', ingredients: 'Sauce tomate, cocktail de mer, oignons, fromage, olives, beurre persillé', normal: '11,50€' },
    { name: 'Niçoise', ingredients: 'Sauce tomate, thon, anchois, oignons, mozzarella, origan, olives', normal: '11,50€', grande: '19,50€' },
    { name: 'Burger', ingredients: 'Sauce tomate, oignons, fromage, viande hachée, olives, sauce burger', normal: '11,50€', grande: '19,50€' },
    { name: 'Thaï', ingredients: 'Sauce tomate, épice Thaï, poivrons, oignons, fromage, viande haché, poulet', normal: '12€', grande: '20€' },
    { name: 'La Bainvilloise', ingredients: 'Sauce tomate, lardons, oignons, chèvre, viande hachée, fromage, olives, pignon de pin', normal: '12€', grande: '20€' },
    { name: 'La Normande', ingredients: 'Sauce tomate, crème, jambon, champignons, ail, persil, fromage et camembert', normal: '12€', grande: '20€' },
    { name: 'Bolognaise', ingredients: 'Sauce tomate, tomate fraîche, viande hachée, fromage, oeuf, olives', normal: '11,50€', grande: '19,50€' },
    { name: 'Orientale', ingredients: 'Sauce tomate, champignons, poivrons, merguez, fromage, olives', normal: '11€', grande: '19€' },
    { name: 'Pizz\'Ajo', ingredients: 'Sauce tomate, champignons, merguez, oignons, chorizo, poivrons, fromage, olives', normal: '12€', grande: '20€' },
  ];

  const specialPizzas = [
    { name: 'La Bollywood', ingredients: 'Sauce curry, oignons, poivrons, mozzarella, merguez, poulet, fromage, olives et origan', normal: '12€', grande: '20€', isNew: true },
    { name: 'L\'Andalouse', ingredients: 'Sauce andalouse, poivrons, merguez, oignons, fromage, viande hachée, olives', normal: '12€', grande: '20€' },
    { name: 'Texane', ingredients: 'Sauce barbecue, oignons, tomate fraîche, cornichons, fromage, viande hachée, cheddar', normal: '12€', grande: '20€' },
    { name: 'L\'Indienne', ingredients: 'Sauce moutarde, curry, tomate fraîche, oignons, fromage, poulet, origan, olives', normal: '12€', grande: '20€' },
    { name: 'L\'Américaine', ingredients: 'Sauce barbecue, oignons, chorizo, fromage, poulet, olives', normal: '12€', grande: '20€' },
  ];

  const gourmetPizzas = [
    { name: 'St Jacques', ingredients: 'Base crème, St jacques, crevette, ail, persil, fromage', normal: '13,50€', grande: '' },
    { name: 'Escargot', ingredients: 'Base crème, escargots, beurre persillé, fromage', normal: '13,50€', grande: '' },
    { name: 'Périgourdine', ingredients: 'Base crème, pomme de terre, magret de canard, gésiers, persil, fromage', normal: '13,50€', grande: '' },
    { name: 'Foie gras (sucré salé)', ingredients: 'Base crème, champignons, foie gras, pain d\'épices, miel, fromage', normal: '13,50€', grande: '' },
  ];

  const cremePizzas = [
    { name: 'L\'Italienne', ingredients: 'Base crème, gorgonzola, mortadelle fraîche, éclats de pistache', normal: '12€', grande: '20€', isNew: true },
    { name: 'Flamme', ingredients: 'Base crème, lardons, oignons', normal: '9,50€', grande: '17,50€' },
    { name: 'Flamme Gratinée', ingredients: 'Base crème, lardons, oignons, fromage', normal: '10€', grande: '18€' },
    { name: 'Douceur', ingredients: 'Base crème, jambon, chèvre, pignon de pins, noix, fromage, miel', normal: '12€', grande: '20€' },
    { name: 'Savoyarde', ingredients: 'Base crème, pommes de terre, lardons, reblochon, fromage', normal: '12€', grande: '20€' },
    { name: 'La Fondante', ingredients: 'Base crème, oignons, lardons, poivrons, fromage, raclette, origan', normal: '12€', grande: '20€' },
    { name: 'Maroilles', ingredients: 'Base crème, pomme de terre, lardons, maroilles, fromage', normal: '12€', grande: '20€' },
    { name: 'Montagnarde', ingredients: 'Base crème, pomme de terre, jambon fumé, oignon, reblochon, fromage', normal: '12€', grande: '20€' },
    { name: 'Raclette', ingredients: 'Base crème, pomme de terre, lardons, raclette, fromage', normal: '12€', grande: '20€' },
    { name: 'Vosgienne', ingredients: 'Base crème, lardons, munster, fromage, olives, oeuf', normal: '12€', grande: '20€' },
    { name: 'Saumon', ingredients: 'Base crème, saumon, oignons, fromage, olives', normal: '11,50€', grande: '19,50€' },
    { name: 'Pizza Kebab', ingredients: 'Base crème, tomate fraîche, viande kebab, oignons, fromage', normal: '12€', grande: '20€' },
    { name: 'Champêtre', ingredients: 'Base crème, lardons, champignons, poulet, oignons, olives', normal: '12€', grande: '20€' },
    { name: 'La Crémeuse', ingredients: 'Base crème, pommes de terre, jambon, oignons, fromage et camembert', normal: '12€', grande: '20€', isNew: true },
  ];

  const burgers = [
    { name: 'Le Savoyard', ingredients: 'Pain brioché, sauce fromagère, salade, steak-haché fermier, tomme de Savoie, lard fumé et oignons frits', price: '13€' },
    { name: 'Le Crispy', ingredients: 'Pain brioché, sauce deluxe, poulet frit, cheddar, salade, tomate fraîche, oignons et lard fumé', price: '13€' },
    { name: 'Le Cheese', ingredients: 'Pain brioché, sauce ketchup/mayo, steak-haché, cheddar et cornichons', price: '10€' },
  ];

  const burgerDuMois = {
    name: 'Burger du mois',
    ingredients: 'Steak ou poulet, fromage au choix, sauce au choix, salade, tomate, oignons',
    price: '13€',
    viandes: ['Steak', 'Poulet'],
    fromages: ['Reblochon', 'Munster', 'Camembert', 'Chèvre', 'Raclette', 'Maroilles'],
    sauces: ['Sauce pomme frite', 'Ketchup', 'Mayonnaise', 'Sauce fromagère', 'Sauce curry'],
    accompagnement: 'Salade, tomate et oignons',
  };

  const desserts = [
    { name: 'Pizza Nutella Smarties', ingredients: 'Base crème, Nutella, smarties, perles multicolore', normal: '10€', grande: '18€' },
    { name: 'Pizza Nutella M&M\'s', ingredients: 'Base crème, Nutella, éclats de M&M\'s, perles multicolores', normal: '10€', grande: '18€' },
    { name: 'Pizza Nutella KitKat', ingredients: 'Base crème, Nutella, éclats de KitKat', normal: '10€', grande: '18€' },
    { name: 'Tiramisu', ingredients: 'Goût au choix en magasin', price: '3,50€' },
    { name: 'Moelleux Chocolat', ingredients: 'servit avec de la crème anglaise', price: '3,50€' },
  ];

  const frites = [
    { name: 'Frites seules', ingredients: 'Portion de frites, accompagné d\'une sauce aux choix', price: '3€' },
    { name: 'Frites en accompagnement', ingredients: 'Portion de frites avec votre burger', price: '2€' },
  ];

  const enfantsMenu = [
    { name: 'Pizza du Coyote — Jambon (20cm)', ingredients: 'Sauce tomate, jambon, fromage', menuIncludes: 'Capri-Sun + surprise', price: '8,50€' },
    { name: 'Pizza du Coyote — Viande hachée (20cm)', ingredients: 'Sauce tomate, viande hachée, fromage', menuIncludes: 'Capri-Sun + surprise', price: '8,50€' },
  ];

  // Extraire tous les ingrédients uniques de toutes les pizzas
  const allIngredients = useMemo(() => {
    const allPizzas = [...tomatePizzas, ...cremePizzas, ...gourmetPizzas, ...specialPizzas];
    const ingredientsSet = new Set<string>();
    
    allPizzas.forEach(pizza => {
      const ingredients = pizza.ingredients
        .split(',')
        .map(ing => ing.trim().toLowerCase())
        .filter(ing => ing.length > 0);
      
      ingredients.forEach(ing => {
        const normalized = ing
          .replace(/^(sauce |base )/, '')
          .trim();
        if (normalized.length > 2) {
          ingredientsSet.add(normalized.charAt(0).toUpperCase() + normalized.slice(1));
        }
      });
    });
    
    return Array.from(ingredientsSet).sort();
  }, []);

  const hasActiveFilters = includedIngredients.length > 0 || excludedIngredients.length > 0;

  // Fonction pour filtrer les pizzas selon les ingrédients
  const filterPizzas = (pizzas: any[]) => {
    if (!hasActiveFilters) return pizzas;
    
    return pizzas.filter(pizza => {
      const pizzaIngredients = pizza.ingredients.toLowerCase();
      
      const hasAllIncluded = includedIngredients.every(ing => 
        pizzaIngredients.includes(ing.toLowerCase())
      );
      
      const hasNoExcluded = excludedIngredients.every(ing => 
        !pizzaIngredients.includes(ing.toLowerCase())
      );
      
      return hasAllIncluded && hasNoExcluded;
    });
  };

  // Filtrer toutes les catégories de pizzas
  const filteredTomatePizzas = filterPizzas(tomatePizzas);
  const filteredCremePizzas = filterPizzas(cremePizzas);
  const filteredGourmetPizzas = filterPizzas(gourmetPizzas);
  const filteredSpecialPizzas = filterPizzas(specialPizzas);

  // Total des pizzas filtrées
  const totalFilteredPizzas = filteredTomatePizzas.length + filteredCremePizzas.length + filteredGourmetPizzas.length + filteredSpecialPizzas.length;

  const getPizzaItems = () => {
    switch (pizzaSubCategory) {
      case 'tomate': return tomatePizzas;
      case 'creme': return cremePizzas;
      case 'gourmet': return gourmetPizzas;
      case 'special': return specialPizzas;
      case 'special-gourmet': return [...specialPizzas, ...gourmetPizzas];
      default: return [];
    }
  };

  const getFilteredPizzaItems = () => {
    switch (pizzaSubCategory) {
      case 'tomate': return filteredTomatePizzas;
      case 'creme': return filteredCremePizzas;
      case 'gourmet': return filteredGourmetPizzas;
      case 'special': return filteredSpecialPizzas;
      case 'special-gourmet': return [...filteredSpecialPizzas, ...filteredGourmetPizzas];
      default: return [];
    }
  };

  const clearFilters = () => {
    setIncludedIngredients([]);
    setExcludedIngredients([]);
  };

  const getPizzaCategoryFromSub = (sub: string) => {
    switch (sub) {
      case 'tomate': return 'pizza-tomate';
      case 'creme': return 'pizza-creme';
      case 'gourmet': return 'pizza-gourmet';
      case 'special': return 'pizza-special';
      case 'special-gourmet': return 'pizza-special';
      default: return 'pizza-tomate';
    }
  };

  const getPizzaCategory = () => {
    return getPizzaCategoryFromSub(pizzaSubCategory || 'tomate');
  };

  const modalContentRef = useRef<HTMLDivElement>(null);
  const categoryModalRef = useRef<HTMLDivElement>(null);

  // Bloquer le scroll de la page en arrière-plan quand une modale est ouverte
  useEffect(() => {
    const isAnyModalOpen = showCategoryModal || burgerNoteModalOpen || showDownloadMenu;
    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showCategoryModal, burgerNoteModalOpen, showDownloadMenu]);

  const handleMainCategoryClick = (category: string) => {
    setMainCategory(category);
    if (category === 'pizzas') {
      setPizzaSubCategory('all');
    }
    setShowCategoryModal(true);
    // Remonter en haut du contenu de la modale
    setTimeout(() => {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTop = 0;
      }
    }, 50);
  };

  // Exposer handleMainCategoryClick au parent via ref
  useImperativeHandle(ref, () => ({
    handleMainCategoryClick,
    scrollToItalienne: () => {
      setMainCategory('pizzas');
      setPizzaSubCategory('creme');
      setShowCategoryModal(true);
      setTimeout(() => {
        if (modalContentRef.current) {
          modalContentRef.current.scrollTop = 0;
        }
      }, 50);
      setTimeout(() => {
        const italienneEl = document.getElementById('italienne-pizza');
        if (italienneEl && modalContentRef.current) {
          italienneEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }));

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setMainCategory(null);
    setPizzaSubCategory(null);
    clearFilters();
  };

  const handleToggleFavorite = (item: any, category: string, size?: 'normal' | 'grande') => {
    const id = `${category}-${item.name}${size ? `-${size}` : ''}`;
    const price = size === 'grande' ? item.grande : (item.normal || item.price);
    
    if (isFavorite(id)) {
      removeFavorite(id, (itemName) => {
        showToast(`${itemName} a été retiré des favoris`, 'info');
      });
    } else {
      if (category === 'burger') {
        setPendingBurgerNote({ item, category });
        setBurgerNoteModalOpen(true);
      } else {
        // Pour les autres produits, ajouter directement
        const favoriteItem: FavoriteItem = {
          id,
          name: item.name,
          ingredients: item.ingredients,
          price,
          category,
          size,
        };
        addFavorite(favoriteItem, (itemName) => {
          showToast(`✓ ${itemName} a bien été ajouté aux favoris`, 'success');
        });
      }
    }
  };

  const handleBurgerNoteConfirm = (note: string) => {
    if (pendingBurgerNote) {
      const { item, category } = pendingBurgerNote;
      const id = `${category}-${item.name}`;
      const favoriteItem: FavoriteItem = {
        id,
        name: item.name,
        ingredients: item.ingredients,
        price: item.price,
        category,
        note: note || undefined,
      };
      addFavorite(favoriteItem, (itemName) => {
        showToast(`✓ ${itemName} a bien été ajouté aux favoris`, 'success');
      });
    }
    setBurgerNoteModalOpen(false);
    setPendingBurgerNote(null);
  };

  const handleBurgerNoteClose = () => {
    setBurgerNoteModalOpen(false);
    setPendingBurgerNote(null);
  };

  const handleDownloadMenu = (card: { id: string; name: string; imageUrl: string }) => {
    setShowDownloadMenu(false);
    setPreviewCard(card);
  };

  const categories = [
    { id: 'pizzas', label: 'Pizzas', icon: 'ri-restaurant-line', color: 'from-terracotta to-chocolate', bgLight: 'bg-rose-soft/30', info: 'Mardi → Dimanche' },
    { id: 'burgers', label: 'Burgers', icon: 'ri-restaurant-2-fill', color: 'from-chocolate to-chocolate-light', bgLight: 'bg-chocolate/10', badge: 'NOUVEAU', info: 'Mardi → Jeudi' },
    { id: 'desserts', label: 'Desserts', icon: 'ri-cake-2-line', color: 'from-rose-soft to-terracotta', bgLight: 'bg-rose-soft/30', badge: 'NOUVEAU' },
    { id: 'frites', label: 'Frites', icon: 'ri-restaurant-2-line', color: 'from-chocolate-light to-terracotta', bgLight: 'bg-chocolate/10', badge: 'NOUVEAU' },
    { id: 'enfants', label: 'Enfants', icon: 'ri-gift-line', color: 'from-terracotta to-rose-soft', bgLight: 'bg-rose-soft/20' },
  ];

  const pizzaSubCategories = [
    { id: 'all', label: 'Toutes nos pizzas', icon: 'ri-apps-fill' },
    { id: 'tomate', label: 'Pizza Base Tomate', icon: 'ri-drop-fill' },
    { id: 'creme', label: 'Pizza Base Crème', icon: 'ri-bowl-fill' },
    { id: 'special-gourmet', label: 'Spéciales & Gourmets', icon: 'ri-star-fill' },
  ];

  const renderPizzaCard = (item: any, category: string, showSize: boolean = true) => {
    // Déterminer la couleur de fond selon la catégorie et la base
    const getBgColor = () => {
      if (category === 'pizza-tomate') return 'bg-rose-soft'; // Rose plus foncé pour base tomate
      if (category === 'pizza-creme') return 'bg-rose-light/60'; // Rose plus clair pour base crème
      if (category === 'pizza-gourmet') {
        // Vérifier si c'est base crème ou tomate
        const isCremeBase = item.ingredients.toLowerCase().includes('base crème');
        return isCremeBase ? 'bg-rose-light/60' : 'bg-rose-soft';
      }
      if (category === 'pizza-special') {
        // Couleurs selon la sauce
        if (item.name === "La Bollywood") return 'bg-sauce-curry'; // Jaune curry
        if (item.name === "L'Indienne") return 'bg-sauce-moutarde'; // Jaune moutarde (plus foncé)
        if (item.name === "L'Andalouse") return 'bg-sauce-andalouse'; // Rose-orangé
        if (item.name === 'Texane' || item.name === "L'Américaine") return 'bg-sauce-barbecue'; // Rose-rouge (BBQ)
        return 'bg-rose-soft';
      }
      return 'bg-rose-soft';
    };

    const getBorderColor = () => {
      if (category === 'pizza-creme') return 'border-rose-light';
      if (category === 'pizza-gourmet') {
        const isCremeBase = item.ingredients.toLowerCase().includes('base crème');
        return isCremeBase ? 'border-rose-light' : 'border-rose-soft';
      }
      if (category === 'pizza-special') {
        if (item.name === "La Bollywood") return 'border-sauce-curry';
        if (item.name === "L'Indienne") return 'border-sauce-moutarde';
        if (item.name === "L'Andalouse") return 'border-sauce-andalouse';
        if (item.name === 'Texane' || item.name === "L'Américaine") return 'border-sauce-barbecue';
      }
      return 'border-rose-soft';
    };

    return (
      <div 
        key={`${category}-${item.name}`}
        id={item.name === "L'Italienne" ? 'italienne-pizza' : undefined}
        className={`group ${getBgColor()} rounded-xl p-3 hover:shadow-md transition-all duration-300 border ${getBorderColor()}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="text-sm font-bold text-chocolate group-hover:text-terracotta transition-colors leading-tight">
                {item.name}
              </h3>
              {item.isNew && (
                <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-white bg-terracotta px-2 py-0.5 rounded-full uppercase tracking-wide shrink-0">
                  <i className="ri-star-fill text-[8px]"></i> Nouveau
                </span>
              )}
              {category === 'pizza-gourmet' && (
                <span className="inline-flex items-center gap-0.5 text-[9px] text-terracotta font-medium shrink-0">
                  <i className="ri-vip-crown-fill"></i> Premium
                </span>
              )}
            </div>
            <p className="text-dark/60 text-xs leading-relaxed">
              {hasActiveFilters ? (
                <HighlightedIngredients 
                  text={item.ingredients} 
                  included={includedIngredients}
                  excluded={excludedIngredients}
                />
              ) : (
                item.ingredients
              )}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-1.5">
              <FavoriteButton
                isFavorite={isFavorite(`${category}-${item.name}-normal`)}
                onToggle={() => handleToggleFavorite(item, category, 'normal')}
                size="sm"
              />
              <div className="text-center min-w-[40px]">
                {item.grande && showSize && <p className="text-[9px] text-dark/50 leading-none mb-0.5">29cm</p>}
                <p className="text-sm font-bold text-terracotta">{item.normal}</p>
              </div>
            </div>
            {item.grande && (
              <div className="flex items-center gap-1.5">
                <div className="text-center min-w-[40px]">
                  {showSize && <p className="text-[9px] text-dark/50 leading-none mb-0.5">40cm</p>}
                  <p className="text-sm font-bold text-terracotta">{item.grande}</p>
                </div>
                <FavoriteButton
                  isFavorite={isFavorite(`${category}-${item.name}-grande`)}
                  onToggle={() => handleToggleFavorite(item, category, 'grande')}
                  size="sm"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCategorySection = (title: string, icon: string, pizzas: any[], category: string, showSize: boolean = true) => {
    if (pizzas.length === 0) return null;
    
    return (
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 flex items-center justify-center bg-terracotta/10 rounded-lg">
            <i className={`${icon} text-sm text-terracotta`}></i>
          </div>
          <h3 className="text-sm font-bold text-chocolate">{title}</h3>
          <span className="text-xs text-dark/50">({pizzas.length})</span>
        </div>
        <div className="flex flex-col gap-2" data-product-shop>
          {pizzas.map((item: any) => renderPizzaCard(item, category, showSize))}
        </div>
      </div>
    );
  };

  return (
    <section id="menu" className="py-12 px-4 bg-cream relative overflow-hidden">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}

      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-rose-soft/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-rose-soft/30 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      
      {/* Floating Favorites Button */}
      <button
        onClick={() => setIsPanelOpen(true)}
        className="fixed bottom-6 right-6 z-30 group cursor-pointer"
      >
        <div className="absolute inset-0 bg-terracotta rounded-full blur-lg opacity-50 group-hover:opacity-70 transition-opacity"></div>
        <div className="relative bg-terracotta text-white w-12 h-12 rounded-full shadow-xl flex items-center justify-center transition-all group-hover:scale-110">
          <i className="ri-heart-3-fill text-lg"></i>
          {favorites.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-cream text-terracotta w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center shadow-lg">
              {favorites.length}
            </span>
          )}
        </div>
      </button>

      {/* Modal de note burger */}
      <BurgerNoteModal
        isOpen={burgerNoteModalOpen}
        onClose={handleBurgerNoteClose}
        onConfirm={handleBurgerNoteConfirm}
        burgerName={pendingBurgerNote?.item.name || ''}
      />

      <FavoritesPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        favorites={favorites}
        onRemove={removeFavorite}
        onClear={clearFavorites}
      />

      {/* Prévisualisation plein écran de la carte */}
      {previewCard && (
        <div
          className="fixed inset-0 bg-black/90 z-[90] flex flex-col items-center justify-center"
          onClick={() => setPreviewCard(null)}
        >
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-black/60 z-10">
            <span className="text-white font-semibold text-sm">{previewCard.name}</span>
            <button
              onClick={() => setPreviewCard(null)}
              className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg cursor-pointer"
            >
              <i className="ri-close-line text-white text-lg"></i>
            </button>
          </div>

          {/* Image (pivotée en paysage pour les non-boissons) */}
          <div
            className="flex items-center justify-center w-full h-full px-2 pt-12 pb-16"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={previewCard.imageUrl}
              alt={previewCard.name}
              className="max-w-full max-h-full object-contain rounded-lg select-none"
              style={
                previewCard.id !== 'boissons'
                  ? {
                      transform: 'rotate(90deg)',
                      maxWidth: '85vh',
                      maxHeight: '95vw',
                    }
                  : {}
              }
              draggable={false}
            />
          </div>

          {/* Instruction bas */}
          <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-4 py-3 text-center">
            <p className="text-white/80 text-xs leading-relaxed">
              <i className="ri-hand-coin-line mr-1"></i>
              <strong className="text-white">Appuyez longuement</strong> sur l&apos;image pour l&apos;enregistrer dans votre galerie
            </p>
          </div>
        </div>
      )}

      {/* Modal téléchargement menu */}
      {showDownloadMenu && (
        <>
          <div 
            className="fixed inset-0 bg-dark/30 backdrop-blur-sm z-40"
            onClick={() => setShowDownloadMenu(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-chocolate p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <i className="ri-file-image-line text-xl text-white"></i>
                  <span className="text-white font-semibold text-sm">Voir la carte</span>
                </div>
                <button 
                  onClick={() => setShowDownloadMenu(false)}
                  className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-lg hover:bg-white/30 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-white"></i>
                </button>
              </div>
              
              <div className="p-4 space-y-2">
                {menuCards.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => handleDownloadMenu(card)}
                    className="w-full flex items-center justify-between p-3 bg-cream/50 hover:bg-cream rounded-xl transition-colors cursor-pointer"
                  >
                    <span className="text-chocolate text-sm font-medium">{card.name}</span>
                    <i className="ri-fullscreen-line text-terracotta"></i>
                  </button>
                ))}
                <p className="text-center text-dark/50 text-[10px] pt-1 leading-relaxed">
                  <i className="ri-information-line mr-0.5"></i>
                  L&apos;image s&apos;ouvre en grand — appuyez longuement pour sauvegarder
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* MODAL CATÉGORIE */}
      {showCategoryModal && mainCategory && (
        <>
          <div 
            className="fixed inset-0 bg-dark/40 backdrop-blur-sm z-40"
            onClick={closeCategoryModal}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div 
              ref={categoryModalRef}
              className="bg-[#FDF8F3] rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header de la modale */}
              <div className="bg-terracotta p-4 flex items-center justify-between sticky top-0 z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                    <i className={`${categories.find(c => c.id === mainCategory)?.icon} text-xl text-white`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white font-display">
                      {categories.find(c => c.id === mainCategory)?.label}
                    </h3>
                    <p className="text-white/70 text-xs">Ajoutez vos favoris et appelez-nous</p>
                  </div>
                </div>
                <button 
                  onClick={closeCategoryModal}
                  className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-xl hover:bg-white/30 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-white"></i>
                </button>
              </div>

              {/* Contenu de la modale — ref pour reset scroll */}
              <div ref={modalContentRef} className="p-4 overflow-y-auto max-h-[calc(85vh-80px)]">
                {/* Pizza Section */}
                {mainCategory === 'pizzas' && (
                  <div>
                    {/* Filtres dans un cadre distinct */}
                    {!hasActiveFilters && (
                      <div className="mb-4 bg-cream border-2 border-chocolate/15 rounded-xl p-3">
                        <p className="text-[10px] font-bold text-chocolate/60 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                          <i className="ri-filter-3-line text-terracotta"></i>
                          Filtrer par type
                        </p>
                        <div className="space-y-1.5">
                          {/* Toutes nos pizzas */}
                          <button
                            onClick={() => setPizzaSubCategory('all')}
                            className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-full font-medium text-xs transition-all cursor-pointer ${
                              pizzaSubCategory === 'all'
                                ? 'bg-terracotta text-white shadow-sm shadow-terracotta/25'
                                : 'bg-white text-chocolate hover:bg-rose-soft/80 border border-chocolate/10'
                            }`}
                          >
                            <i className="ri-apps-fill text-xs"></i>
                            Toutes nos pizzas
                          </button>

                          {/* Base Tomate + Base Crème */}
                          <div className="flex gap-1.5">
                            {[
                              { id: 'tomate', label: 'Base Tomate', icon: 'ri-drop-fill' },
                              { id: 'creme', label: 'Base Crème', icon: 'ri-bowl-fill' },
                            ].map((sub) => (
                              <button
                                key={sub.id}
                                onClick={() => setPizzaSubCategory(pizzaSubCategory === sub.id ? 'all' : sub.id)}
                                className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-full font-medium text-[10px] transition-all cursor-pointer ${
                                  pizzaSubCategory === sub.id
                                    ? 'bg-terracotta text-white shadow-sm shadow-terracotta/25'
                                    : 'bg-white text-chocolate hover:bg-rose-soft/80 border border-chocolate/10'
                                }`}
                              >
                                <i className={`${sub.icon} text-xs`}></i>
                                {sub.label}
                              </button>
                            ))}
                          </div>

                          {/* Spéciales & Gourmets */}
                          <button
                            onClick={() => setPizzaSubCategory(pizzaSubCategory === 'special-gourmet' ? 'all' : 'special-gourmet')}
                            className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full font-medium text-[10px] transition-all cursor-pointer ${
                              pizzaSubCategory === 'special-gourmet'
                                ? 'bg-terracotta text-white shadow-sm shadow-terracotta/25'
                                : 'bg-white text-chocolate hover:bg-rose-soft/80 border border-chocolate/10'
                            }`}
                          >
                            <i className="ri-star-fill text-xs"></i>
                            Spéciales &amp; Gourmets
                          </button>
                        </div>

                        {/* Ingredient Filter */}
                        <div className="mt-3 pt-3 border-t border-chocolate/10">
                          <p className="text-[10px] font-bold text-chocolate/60 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                            <i className="ri-search-line text-terracotta"></i>
                            Filtrer par ingrédient
                          </p>
                          <IngredientFilter
                            allIngredients={allIngredients}
                            includedIngredients={includedIngredients}
                            excludedIngredients={excludedIngredients}
                            onIncludedChange={setIncludedIngredients}
                            onExcludedChange={setExcludedIngredients}
                            onClear={clearFilters}
                          />
                        </div>
                      </div>
                    )}

                    {/* Ingredient Filter when active filters */}
                    {hasActiveFilters && (
                      <div className="mb-3 bg-cream border-2 border-terracotta/30 rounded-xl p-3">
                        <p className="text-[10px] font-bold text-terracotta uppercase tracking-wider mb-2 flex items-center gap-1.5">
                          <i className="ri-filter-fill text-terracotta"></i>
                          Filtres actifs
                        </p>
                        <IngredientFilter
                          allIngredients={allIngredients}
                          includedIngredients={includedIngredients}
                          excludedIngredients={excludedIngredients}
                          onIncludedChange={setIncludedIngredients}
                          onExcludedChange={setExcludedIngredients}
                          onClear={clearFilters}
                        />
                      </div>
                    )}

                    {/* Filter Results Info */}
                    {hasActiveFilters && (
                      <div className={`mb-3 p-2 rounded-lg flex items-center justify-between ${
                        totalFilteredPizzas > 0 ? 'bg-terracotta/10' : 'bg-chocolate/10'
                      }`}>
                        <div className="flex items-center gap-1.5">
                          <div className={`w-5 h-5 flex items-center justify-center rounded ${
                            totalFilteredPizzas > 0 ? 'bg-terracotta/20' : 'bg-chocolate/20'
                          }`}>
                            <i className={`ri-restaurant-2-line text-[10px] ${
                              totalFilteredPizzas > 0 ? 'text-terracotta' : 'text-chocolate'
                            }`}></i>
                          </div>
                          <p className={`font-medium text-[10px] ${
                            totalFilteredPizzas > 0 ? 'text-terracotta' : 'text-chocolate'
                          }`}>
                            {totalFilteredPizzas > 0 
                              ? `${totalFilteredPizzas} pizza${totalFilteredPizzas > 1 ? 's' : ''} trouvée${totalFilteredPizzas > 1 ? 's' : ''}`
                              : 'Aucune pizza trouvée'}
                          </p>
                        </div>
                        <button
                          onClick={clearFilters}
                          className="text-[10px] text-dark/60 hover:text-chocolate font-medium cursor-pointer flex items-center gap-0.5"
                        >
                          <i className="ri-refresh-line text-xs"></i>
                          Réinitialiser
                        </button>
                      </div>
                    )}

                    {/* Display logic */}
                    {hasActiveFilters ? (
                      <div>
                        {totalFilteredPizzas > 0 ? (
                          <>
                            {renderCategorySection('Pizza Base Tomate', 'ri-drop-fill', filteredTomatePizzas, 'pizza-tomate')}
                            {renderCategorySection('Pizza Base Crème', 'ri-bowl-fill', filteredCremePizzas, 'pizza-creme')}
                            {renderCategorySection('Pizzas Spéciales & Gourmets', 'ri-star-fill', [...filteredSpecialPizzas, ...filteredGourmetPizzas], 'pizza-special')}
                          </>
                        ) : (
                          <div className="text-center py-6 bg-rose-soft/50 rounded-lg border border-dashed border-chocolate/20">
                            <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-cream rounded-xl">
                              <i className="ri-emotion-sad-line text-lg text-dark/30"></i>
                            </div>
                            <p className="text-dark/50 text-sm font-medium mb-1">Aucune pizza trouvée</p>
                            <p className="text-dark/40 text-[10px] mb-2">Modifiez vos critères</p>
                            <button
                              onClick={clearFilters}
                              className="px-3 py-1.5 bg-terracotta text-white rounded-full text-[10px] font-medium cursor-pointer hover:bg-terracotta/90 transition-colors whitespace-nowrap"
                            >
                              <i className="ri-refresh-line mr-0.5"></i>
                              Voir toutes les pizzas
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {pizzaSubCategory === 'all' && (
                          <>
                            <div className="bg-rose-soft/50 rounded-lg p-2 mb-3 flex justify-center items-center gap-4 border border-rose-soft">
                              <div className="flex items-center gap-1.5">
                                <div className="w-5 h-5 flex items-center justify-center bg-cream rounded">
                                  <span className="text-dark/60 font-bold text-[10px]">29</span>
                                </div>
                                <span className="text-dark/60 font-medium text-[10px]">Normale</span>
                              </div>
                              <div className="w-px h-4 bg-rose-soft"></div>
                              <div className="flex items-center gap-1.5">
                                <div className="w-5 h-5 flex items-center justify-center bg-terracotta/10 rounded">
                                  <span className="text-terracotta font-bold text-[10px]">40</span>
                                </div>
                                <span className="text-dark/60 font-medium text-[10px]">Grande</span>
                              </div>
                            </div>

                            {renderCategorySection('Pizza Base Tomate', 'ri-drop-fill', tomatePizzas, 'pizza-tomate')}
                            {renderCategorySection('Pizza Base Crème', 'ri-bowl-fill', cremePizzas, 'pizza-creme')}
                            {renderCategorySection('Pizzas Spéciales', 'ri-star-fill', specialPizzas, 'pizza-special')}
                            {renderCategorySection('Pizzas Gourmets', 'ri-vip-crown-fill', gourmetPizzas, 'pizza-gourmet', false)}
                          </>
                        )}

                        {pizzaSubCategory && pizzaSubCategory !== 'all' && (
                          <>
                            {(pizzaSubCategory === 'tomate' || pizzaSubCategory === 'creme') && (
                              <div className="bg-rose-soft/50 rounded-lg p-2 mb-3 flex justify-center items-center gap-4 border border-rose-soft">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-5 h-5 flex items-center justify-center bg-cream rounded">
                                    <span className="text-dark/60 font-bold text-[10px]">29</span>
                                  </div>
                                  <span className="text-dark/60 font-medium text-[10px]">Normale</span>
                                </div>
                                <div className="w-px h-4 bg-rose-soft"></div>
                                <div className="flex items-center gap-1.5">
                                  <div className="w-5 h-5 flex items-center justify-center bg-terracotta/10 rounded">
                                    <span className="text-terracotta font-bold text-[10px]">40</span>
                                  </div>
                                  <span className="text-dark/60 font-medium text-[10px]">Grande</span>
                                </div>
                              </div>
                            )}

                            {pizzaSubCategory === 'special-gourmet' ? (
                              <>
                                {renderCategorySection('Pizzas Spéciales', 'ri-star-fill', specialPizzas, 'pizza-special')}
                                {renderCategorySection('Pizzas Gourmets', 'ri-vip-crown-fill', gourmetPizzas, 'pizza-gourmet', false)}
                              </>
                            ) : (
                              <div className="flex flex-col gap-1.5" data-product-shop>
                                {getPizzaItems().map((item) => renderPizzaCard(item, getPizzaCategory(), pizzaSubCategory !== 'gourmet'))}
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Burgers Section */}
                {mainCategory === 'burgers' && (
                  <div>
                    {/* Disponibilité burgers */}
                    <div className="mb-3 bg-chocolate/8 border border-chocolate/25 rounded-xl px-3 py-2.5 flex items-center gap-2.5">
                      <div className="w-7 h-7 flex items-center justify-center bg-chocolate/15 rounded-lg shrink-0">
                        <i className="ri-calendar-line text-sm text-chocolate"></i>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-chocolate leading-tight">Disponibles uniquement en semaine</p>
                        <p className="text-[9px] text-chocolate/65 leading-tight">Du <strong>mardi au jeudi</strong> inclus</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 flex items-center justify-center bg-terracotta/10 rounded-lg">
                        <i className="ri-restaurant-2-fill text-sm text-terracotta"></i>
                      </div>
                      <h3 className="text-sm font-bold text-chocolate">Nos Burgers</h3>
                      <span className="text-xs text-dark/50">({burgers.length})</span>
                    </div>
                    <div className="flex flex-col gap-2" data-product-shop>
                      {/* Burger du mois — à composer soi-même */}
                      <div className="group bg-rose-soft rounded-xl p-4 border-2 border-rose-soft hover:border-terracotta/40 hover:shadow-md transition-all duration-300">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="text-sm font-bold text-chocolate group-hover:text-terracotta transition-colors leading-tight">
                                Burger du mois
                              </h3>
                              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-white bg-terracotta px-2 py-0.5 rounded-full uppercase tracking-wide shrink-0">
                                <i className="ri-star-fill text-[8px]"></i> Nouveau
                              </span>
                              <span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-terracotta shrink-0">
                                <i className="ri-settings-3-line"></i> À composer
                              </span>
                            </div>
                            <p className="text-dark/60 text-xs leading-relaxed">
                              Composez votre burger vous-même !
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <FavoriteButton
                              isFavorite={isFavorite('burger-Burger du mois')}
                              onToggle={() => handleToggleFavorite(burgerDuMois, 'burger')}
                              size="sm"
                            />
                            <p className="text-sm font-bold text-terracotta min-w-[40px] text-center">13€</p>
                          </div>
                        </div>

                        <div className="space-y-2.5">
                          <div>
                            <p className="text-[10px] font-bold text-chocolate/60 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                              <span className="w-4 h-4 flex items-center justify-center bg-terracotta/15 rounded text-terracotta text-[9px] font-bold">1</span>
                              Choisissez votre viande
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {burgerDuMois.viandes.map((v) => (
                                <span key={v} className="bg-white/80 text-chocolate text-[11px] font-medium px-2.5 py-1 rounded-full border border-chocolate/15">{v}</span>
                              ))}
                            </div>
                          </div>

                          <div>
                            <p className="text-[10px] font-bold text-chocolate/60 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                              <span className="w-4 h-4 flex items-center justify-center bg-terracotta/15 rounded text-terracotta text-[9px] font-bold">2</span>
                              Choisissez votre fromage
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {burgerDuMois.fromages.map((f) => (
                                <span key={f} className="bg-white/80 text-chocolate text-[11px] font-medium px-2.5 py-1 rounded-full border border-chocolate/15">{f}</span>
                              ))}
                            </div>
                          </div>

                          <div>
                            <p className="text-[10px] font-bold text-chocolate/60 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                              <span className="w-4 h-4 flex items-center justify-center bg-terracotta/15 rounded text-terracotta text-[9px] font-bold">3</span>
                              Choisissez votre sauce
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {burgerDuMois.sauces.map((s) => (
                                <span key={s} className="bg-white/80 text-chocolate text-[11px] font-medium px-2.5 py-1 rounded-full border border-chocolate/15">{s}</span>
                              ))}
                            </div>
                          </div>

                          <div className="bg-white/60 rounded-lg px-3 py-2 border border-chocolate/10">
                            <p className="text-[11px] text-chocolate/80">
                              <i className="ri-checkbox-circle-fill text-terracotta text-xs mr-1"></i>
                              <strong>Accompagné de :</strong> {burgerDuMois.accompagnement}
                            </p>
                          </div>
                        </div>
                      </div>

                      {burgers.map((item) => (
                        <div 
                          key={`burger-${item.name}`}
                          className="group bg-rose-soft rounded-xl p-3 hover:shadow-md transition-all duration-300 border border-rose-soft"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-bold text-chocolate group-hover:text-terracotta transition-colors leading-tight mb-1">
                                {item.name}
                              </h3>
                              <p className="text-dark/60 text-xs leading-relaxed">
                                {item.ingredients}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <FavoriteButton
                                isFavorite={isFavorite(`burger-${item.name}`)}
                                onToggle={() => handleToggleFavorite(item, 'burger')}
                                size="sm"
                              />
                              <p className="text-sm font-bold text-terracotta min-w-[40px] text-center">{item.price}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Supplément frites en accompagnement */}
                    <div className="mt-4 bg-cream/60 rounded-lg p-3 border border-dashed border-chocolate/20">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 flex items-center justify-center bg-terracotta/10 rounded-lg">
                          <i className="ri-add-circle-line text-sm text-terracotta"></i>
                        </div>
                        <h4 className="text-xs font-bold text-chocolate">Supplément</h4>
                      </div>
                      <div className="flex items-start justify-between gap-4 bg-rose-soft rounded-xl p-3 border border-rose-soft">
                        <div className="flex-1 min-w-0">
                          <h5 className="text-sm font-bold text-chocolate">Frites en accompagnement</h5>
                          <p className="text-dark/60 text-xs">Portion de frites avec votre burger</p>
                        </div>
                        <p className="text-sm font-bold text-terracotta min-w-[40px] text-center shrink-0">2€</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Desserts Section */}
                {mainCategory === 'desserts' && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 flex items-center justify-center bg-terracotta/10 rounded-lg">
                        <i className="ri-cake-2-line text-sm text-terracotta"></i>
                      </div>
                      <h3 className="text-sm font-bold text-chocolate">Nos Desserts</h3>
                      <span className="text-xs text-dark/50">({desserts.length})</span>
                    </div>
                    <div className="flex flex-col gap-2" data-product-shop>
                      {desserts.map((item) => (
                        <div 
                          key={`dessert-${item.name}`}
                          className="group bg-rose-soft rounded-xl p-3 hover:shadow-md transition-all duration-300 border border-rose-soft"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-bold text-chocolate group-hover:text-terracotta transition-colors leading-tight mb-1">
                                {item.name}
                              </h3>
                              <p className="text-dark/60 text-xs leading-relaxed">
                                {item.ingredients}
                              </p>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              {item.price ? (
                                <div className="flex items-center gap-2">
                                  <FavoriteButton
                                    isFavorite={isFavorite(`dessert-${item.name}`)}
                                    onToggle={() => handleToggleFavorite(item, 'dessert')}
                                    size="sm"
                                  />
                                  <p className="text-sm font-bold text-terracotta min-w-[40px] text-center">{item.price}</p>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center gap-1.5">
                                    <FavoriteButton
                                      isFavorite={isFavorite(`dessert-${item.name}-normal`)}
                                      onToggle={() => handleToggleFavorite(item, 'dessert', 'normal')}
                                      size="sm"
                                    />
                                    <div className="text-center min-w-[40px]">
                                      <p className="text-[9px] text-dark/50 leading-none mb-0.5">29cm</p>
                                      <p className="text-sm font-bold text-terracotta">{item.normal}</p>
                                    </div>
                                  </div>
                                  {item.grande && (
                                    <div className="flex items-center gap-1.5">
                                      <div className="text-center min-w-[40px]">
                                        <p className="text-[9px] text-dark/50 leading-none mb-0.5">40cm</p>
                                        <p className="text-sm font-bold text-terracotta">{item.grande}</p>
                                      </div>
                                      <FavoriteButton
                                        isFavorite={isFavorite(`dessert-${item.name}-grande`)}
                                        onToggle={() => handleToggleFavorite(item, 'dessert', 'grande')}
                                        size="sm"
                                      />
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Frites Section */}
                {mainCategory === 'frites' && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 flex items-center justify-center bg-terracotta/10 rounded-lg">
                        <i className="ri-restaurant-2-line text-sm text-terracotta"></i>
                      </div>
                      <h3 className="text-sm font-bold text-chocolate">Nos Frites</h3>
                      <span className="text-xs text-dark/50">({frites.length})</span>
                    </div>
                    <div className="flex flex-col gap-2" data-product-shop>
                      {frites.map((item) => (
                        <div 
                          key={`frite-${item.name}`}
                          className="group bg-rose-soft rounded-xl p-3 hover:shadow-md transition-all duration-300 border border-rose-soft"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-bold text-chocolate group-hover:text-terracotta transition-colors leading-tight mb-1">
                                {item.name}
                              </h3>
                              <p className="text-dark/60 text-xs leading-relaxed">
                                {item.ingredients}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <FavoriteButton
                                isFavorite={isFavorite(`frite-${item.name}`)}
                                onToggle={() => handleToggleFavorite(item, 'frite')}
                                size="sm"
                              />
                              <p className="text-sm font-bold text-terracotta min-w-[40px] text-center">{item.price}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Enfants Section */}
                {mainCategory === 'enfants' && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 flex items-center justify-center bg-terracotta/10 rounded-lg">
                        <i className="ri-gift-line text-sm text-terracotta"></i>
                      </div>
                      <h3 className="text-sm font-bold text-chocolate">Menu Enfant</h3>
                      <span className="text-xs text-dark/50">({enfantsMenu.length})</span>
                    </div>
                    <div className="bg-terracotta/10 rounded-lg p-2 mb-3 flex items-center gap-2 border border-terracotta/20">
                      <div className="w-5 h-5 flex items-center justify-center bg-terracotta/20 rounded-md shrink-0">
                        <i className="ri-information-line text-[10px] text-terracotta"></i>
                      </div>
                      <p className="text-[10px] text-chocolate/80 leading-snug">
                        <strong className="text-terracotta">Menu complet à 8,50€</strong> : pizza 20cm + Capri-Sun + surprise
                      </p>
                    </div>
                    <div className="flex flex-col gap-2" data-product-shop>
                      {enfantsMenu.map((item) => (
                        <div 
                          key={`enfant-${item.name}`}
                          className="group bg-rose-soft rounded-xl p-3 hover:shadow-md transition-all duration-300 border border-rose-soft"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-bold text-chocolate group-hover:text-terracotta transition-colors leading-tight mb-1">
                                {item.name}
                              </h3>
                              <p className="text-dark/60 text-xs leading-relaxed">
                                {item.ingredients}
                              </p>
                              <div className="flex items-center gap-1.5 mt-1.5">
                                <span className="inline-flex items-center gap-1 text-[9px] font-medium text-terracotta bg-terracotta/10 px-2 py-0.5 rounded-full">
                                  <i className="ri-gift-line text-[8px]"></i>
                                  {item.menuIncludes}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <FavoriteButton
                                isFavorite={isFavorite(`enfant-${item.name}`)}
                                onToggle={() => handleToggleFavorite(item, 'enfant')}
                                size="sm"
                              />
                              <p className="text-sm font-bold text-terracotta min-w-[40px] text-center">{item.price}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Section Title */}
                <div className="flex items-center gap-3 mb-4 mt-4">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent to-chocolate/20"></div>
                  <h3 className="text-xs font-bold text-chocolate uppercase tracking-[0.15em] flex items-center gap-2 px-3 py-1 bg-cream/50 rounded-full border border-chocolate/10">
                    <i className="ri-restaurant-line text-terracotta text-sm"></i>
                    Nos Produits
                  </h3>
                  <div className="flex-1 h-px bg-gradient-to-l from-transparent to-chocolate/20"></div>
                </div>

                {/* Main Categories - Boutons qui ouvrent les modales */}
                <div className="flex flex-col gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleMainCategoryClick(cat.id)}
                      className="group relative px-4 py-3.5 rounded-lg font-semibold transition-all duration-300 cursor-pointer overflow-hidden bg-cream/70 text-chocolate hover:bg-terracotta hover:text-white border border-chocolate/20 hover:border-terracotta"
                    >
                      <div className="relative flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg transition-all bg-white/80 border border-chocolate/10 group-hover:bg-white/20 group-hover:border-transparent">
                          <i className={`${cat.icon} text-lg text-terracotta group-hover:text-white`}></i>
                        </div>
                        <span className="text-sm font-semibold tracking-wide">{cat.label}</span>
                        {cat.badge && (
                          <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-terracotta/15 text-terracotta group-hover:bg-white/20 group-hover:text-white">
                            {cat.badge}
                          </span>
                        )}
                        {(cat as any).info && (
                          <span className="ml-auto flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full font-semibold bg-chocolate/10 text-chocolate/70 group-hover:bg-white/20 group-hover:text-white whitespace-nowrap">
                            <i className="ri-time-line text-[9px]"></i>
                            {(cat as any).info}
                          </span>
                        )}
                        {!(cat as any).info && <i className="ri-arrow-right-s-line ml-auto text-lg"></i>}
                        {(cat as any).info && <i className="ri-arrow-right-s-line text-lg"></i>}
                      </div>
                    </button>
                  ))}
                </div>

                <div className="mt-5 bg-cream/60 rounded-lg p-3 text-center border border-dashed border-chocolate/20">
                  <div className="flex items-center justify-center gap-2">
                    <i className="ri-add-circle-line text-terracotta text-sm"></i>
                    <p className="text-chocolate/70 text-[11px]">
                      <strong className="text-chocolate">Suppléments :</strong> +1€ (29cm) / +2€ (40cm)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Conteneur principal avec encadré */}
      <div className="max-w-6xl mx-auto relative">
        {/* Carte de menu style restaurant */}
        <div className="relative">
          {/* Ombre portée de la carte */}
          <div className="absolute inset-0 bg-chocolate/10 rounded-lg transform translate-x-1 translate-y-1 blur-sm"></div>
          
          {/* Carte principale */}
          <div className="relative bg-[#FDF8F3] rounded-lg overflow-hidden" style={{ boxShadow: '0 4px 20px rgba(139, 90, 43, 0.15)' }}>
            {/* Bordure décorative extérieure */}
            <div className="absolute inset-0 border-[3px] border-chocolate/25 rounded-lg pointer-events-none"></div>
            <div className="absolute inset-[5px] border border-chocolate/15 rounded pointer-events-none"></div>
            
            {/* Coins décoratifs */}
            <div className="absolute top-2 left-2 w-6 h-6 border-l-2 border-t-2 border-terracotta/50 rounded-tl"></div>
            <div className="absolute top-2 right-2 w-6 h-6 border-r-2 border-t-2 border-terracotta/50 rounded-tr"></div>
            <div className="absolute bottom-2 left-2 w-6 h-6 border-l-2 border-b-2 border-terracotta/50 rounded-bl"></div>
            <div className="absolute bottom-2 right-2 w-6 h-6 border-r-2 border-b-2 border-terracotta/50 rounded-br"></div>
            
            {/* Texture papier subtile */}
            <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }}></div>
            
            {/* Contenu de la carte */}
            <div className="relative p-5 md:p-7">
              {/* Header avec ornements */}
              <div className="text-center mb-6">
                {/* Ornement supérieur */}
                <div className="flex items-center justify-center gap-3 mb-3">
                  <div className="flex items-center gap-1">
                    <div className="w-8 h-px bg-gradient-to-r from-transparent to-terracotta/60"></div>
                    <i className="ri-leaf-line text-terracotta/60 text-xs"></i>
                  </div>
                  <span className="text-terracotta font-semibold text-[10px] uppercase tracking-[0.2em]">
                    Pizz'ajo
                  </span>
                  <div className="flex items-center gap-1">
                    <i className="ri-leaf-line text-terracotta/60 text-xs transform scale-x-[-1]"></i>
                    <div className="w-8 h-px bg-gradient-to-l from-transparent to-terracotta/60"></div>
                  </div>
                </div>
                
                {/* Titre principal */}
                <div className="relative inline-block mb-3">
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-chocolate tracking-wide">
                    Notre Carte
                  </h2>
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-24 h-0.5 bg-gradient-to-r from-transparent via-terracotta to-transparent"></div>
                </div>
                
                <p className="text-chocolate/60 text-xs max-w-xl mx-auto leading-relaxed italic">
                  Des pizzas artisanales préparées avec amour, des burgers gourmands et des desserts irrésistibles
                </p>
              </div>

              {/* Séparateur décoratif */}
              <div className="flex items-center justify-center gap-2 mb-5">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent to-chocolate/20"></div>
                <div className="flex items-center gap-1">
                  <i className="ri-star-s-fill text-terracotta/40 text-[8px]"></i>
                  <i className="ri-restaurant-2-fill text-terracotta text-sm"></i>
                  <i className="ri-star-s-fill text-terracotta/40 text-[8px]"></i>
                </div>
                <div className="flex-1 h-px bg-gradient-to-l from-transparent to-chocolate/20"></div>
              </div>

              {/* Favorites Info — compact */}
              <div className="bg-rose-soft/20 rounded-lg px-3 py-2 mb-3 border border-terracotta/30">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center bg-terracotta/15 rounded-md shrink-0">
                    <i className="ri-heart-3-line text-sm text-terracotta"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-chocolate text-[10px] leading-relaxed">
                      <strong>Commandes par téléphone uniquement</strong>
                    </p>
                    <p className="text-terracotta text-[11px] font-bold leading-relaxed">
                      03 60 53 27 92
                    </p>
                    <p className="text-chocolate/70 text-[10px] leading-relaxed">
                      Ajoutez vos favoris pour vous en souvenir !
                    </p>
                  </div>
                </div>
              </div>

              {/* Disponibilités — Pizzas & Burgers */}
              <div className="mb-4 rounded-xl overflow-hidden border border-chocolate/20">
                <div className="bg-chocolate/10 px-3 py-1.5 flex items-center gap-1.5">
                  <i className="ri-time-line text-xs text-chocolate/70"></i>
                  <span className="text-[9px] font-bold text-chocolate/70 uppercase tracking-wider">Disponibilités</span>
                </div>
                <div className="flex divide-x divide-chocolate/15">
                  {/* Pizzas */}
                  <div className="flex-1 bg-terracotta/8 px-3 py-2.5 flex flex-col items-center gap-1">
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 flex items-center justify-center bg-terracotta/15 rounded-md">
                        <i className="ri-restaurant-line text-[10px] text-terracotta"></i>
                      </div>
                      <span className="text-[10px] font-bold text-chocolate">Pizzas</span>
                    </div>
                    <p className="text-[9px] text-chocolate/70 text-center leading-snug">
                      <strong className="text-terracotta">6 jours sur 7</strong>
                    </p>
                    <p className="text-[9px] text-chocolate/70 text-center leading-snug">
                      Mardi au Dimanche
                    </p>
                  </div>
                  {/* Burgers */}
                  <div className="flex-1 bg-chocolate/5 px-3 py-2.5 flex flex-col items-center gap-1">
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 flex items-center justify-center bg-chocolate/15 rounded-md">
                        <i className="ri-restaurant-2-fill text-[10px] text-chocolate"></i>
                      </div>
                      <span className="text-[10px] font-bold text-chocolate">Burgers</span>
                    </div>
                    <p className="text-[9px] text-chocolate/70 text-center leading-snug">
                      <strong className="text-chocolate">Semaine uniquement</strong>
                    </p>
                    <p className="text-[9px] text-chocolate/70 text-center leading-snug">
                      Mardi au Jeudi
                    </p>
                  </div>
                </div>
              </div>

              {/* Section Title */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent to-chocolate/20"></div>
                <h3 className="text-xs font-bold text-chocolate uppercase tracking-[0.15em] flex items-center gap-2 px-3 py-1 bg-cream/50 rounded-full border border-chocolate/10">
                  <i className="ri-restaurant-line text-terracotta text-sm"></i>
                  Nos Produits
                </h3>
                <div className="flex-1 h-px bg-gradient-to-l from-transparent to-chocolate/20"></div>
              </div>

              {/* Main Categories - Boutons qui ouvrent les modales */}
              <div className="flex flex-col gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleMainCategoryClick(cat.id)}
                    className="group relative px-4 py-3.5 rounded-lg font-semibold transition-all duration-300 cursor-pointer overflow-hidden bg-cream/70 text-chocolate hover:bg-terracotta hover:text-white border border-chocolate/20 hover:border-terracotta"
                  >
                    <div className="relative flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg transition-all bg-white/80 border border-chocolate/10 group-hover:bg-white/20 group-hover:border-transparent">
                        <i className={`${cat.icon} text-lg text-terracotta group-hover:text-white`}></i>
                      </div>
                      <span className="text-sm font-semibold tracking-wide">{cat.label}</span>
                      {cat.badge && (
                        <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-terracotta/15 text-terracotta group-hover:bg-white/20 group-hover:text-white">
                          {cat.badge}
                        </span>
                      )}
                      {(cat as any).info && (
                        <span className="ml-auto flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full font-semibold bg-chocolate/10 text-chocolate/70 group-hover:bg-white/20 group-hover:text-white whitespace-nowrap">
                          <i className="ri-time-line text-[9px]"></i>
                          {(cat as any).info}
                        </span>
                      )}
                      {!(cat as any).info && <i className="ri-arrow-right-s-line ml-auto text-lg"></i>}
                      {(cat as any).info && <i className="ri-arrow-right-s-line text-lg"></i>}
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-5 bg-cream/60 rounded-lg p-3 text-center border border-dashed border-chocolate/20">
                <div className="flex items-center justify-center gap-2">
                  <i className="ri-add-circle-line text-terracotta text-sm"></i>
                  <p className="text-chocolate/70 text-[11px]">
                    <strong className="text-chocolate">Suppléments :</strong> +1€ (29cm) / +2€ (40cm)
                  </p>
                </div>
              </div>

              {/* Bouton Voir la carte en image — en bas */}
              <div className="flex justify-center mt-5">
                <button
                  onClick={() => setShowDownloadMenu(true)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-terracotta hover:bg-chocolate text-white rounded-full transition-all duration-300 cursor-pointer shadow-md shadow-terracotta/30 hover:shadow-lg hover:shadow-chocolate/30 hover:-translate-y-0.5"
                >
                  <i className="ri-file-image-line text-base"></i>
                  <span className="text-sm font-semibold whitespace-nowrap">Voir la carte en image</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

Menu.displayName = 'Menu';

export default Menu;
